import type {
  AnalyticsApplicationStatus,
  CheckReferencesActionOrigins,
  TrackingCurrentPage,
} from 'src/constants/pageTrackingData';
import { createPageAnalyticsFn } from 'src/libs/Analytics';

type CandidateManagementLinkAnalyticEvents =
  | {
      event: 'references_in_candidate_list_pressed';
      attributes: {
        isSeekReferenceApplication: boolean;
        referencesReceived: `${number}:${number}`;
        bucket?: string;
        applicationCorrelationId: string;
        candidateId: number;
        prospectId: string;
        seekListingId: number;
        applicationSeekReferenceStatus: 'standard' | 'limited' | null;
      };
    }
  | {
      event: 'reference_check_pressed';
      attributes: {
        isSeekReferenceJob: boolean;
        seekReferenceBalance: number | null;
        applicationCorrelationId: string;
        bucket?: string;
        candidateId: number;
        prospectId: string;
        currentPage: TrackingCurrentPage;
        jobSeekReferenceStatus: 'standard' | 'limited' | null;
        seekListingId: number;
      };
    }
  | {
      event: 'references_balance_pressed';
      attributes: {
        seekListingId: number;
      };
    }
  | {
      event: 'searched_location_pressed';
      attributes: {
        searchLocationId: string[];
        locationIdSelected: string;
        filterSelection: 'Selected' | 'Deselected';
      };
    }
  | {
      event: 'searched_nationality_pressed';
      attributes: {
        searchNationalityId: string[];
        nationalityIdSelected: string;
        filterSelection: 'Selected' | 'Deselected';
      };
    }
  | {
      event: 'candidate_management_freemium_pop_up_displayed';
      attributes: {
        seekListingId: number;
        alertShownMessageCode:
          | 'Candidate Management - Freemium Upgrade - Hard to Fill'
          | 'Candidate Management - Freemium Upgrade - Urgent Hiring';
      };
    }
  | {
      event: 'candidate_management_freemium_pop_up_upgrade_pressed';
      attributes: {
        seekListingId: number;
        alertShownMessageCode:
          | 'Candidate Management - Freemium Upgrade - Hard to Fill'
          | 'Candidate Management - Freemium Upgrade - Urgent Hiring';
      };
    }
  | {
      event: 'alert_dismiss_pressed';
      attributes: {
        seekListingId: number;
        alertShownMessageCode:
          | 'Candidate Management - Freemium Upgrade - Hard to Fill'
          | 'Candidate Management - Freemium Upgrade - Urgent Hiring';
      };
    }
  | {
      event: 'dialog_dismiss_pressed';
      attributes: {
        seekListingId: number;
        currentPage?: TrackingCurrentPage;
        dialogName:
          | 'Candidate Management - How Reference Checks Work'
          | "Candidate Management - SEEKRef - Couldn't check references";
      };
    }
  | {
      event: 'dialog_dismiss_pressed';
      attributes: {
        seekListingId: number;
        currentPage?: TrackingCurrentPage;
        dialogName:
          | 'Candidate Management - Placement - Update Hiring Status'
          | 'Candidate Management - Placement - Hiring Check-In'
          | 'Candidate Management - Placement - Improve Job Ad'
          | 'Candidate Management - Placement - Improve Next Job Ad'
          | "Candidate Management - Placement - Can't Find The Right Candidate"
          | "Candidate Management - Placement - Can't Display Hiring Status"
          | "Candidate Management - Placement - Couldn't Update Hiring Status";
        hubbleExperiments: {
          cm_improve_direct_placement_signals: {
            name: 'cm_improve_direct_placement_signals';
            participantStatus: 'enrolled';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          };
        };
        experiments: [
          {
            num: 3;
            id: 'cm_improve_direct_placement_signals';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          },
        ];
      };
    }
  | {
      event: 'dialog_dismiss_pressed';
      attributes: {
        isSeekReferenceJob: boolean;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCurrentPage;
        seekListingId: number;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
        dialogName:
          | 'Candidate Management - Order a Reference Check'
          | 'Candidate Management - Reference Check Requested'
          | 'Candidate Management - Request a Limited Reference Check'
          | 'Candidate Management - Limited Reference Check Requested'
          | null;
        jobSeekReferenceStatus: 'standard' | 'limited' | null;
      };
    }
  | {
      event: 'option_selection_pressed';
      attributes: {
        seekListingId: number;
        optionSelected:
          | 'Yes'
          | 'No'
          | 'Not yet'
          | 'Too early to say'
          | 'Still reviewing applications';
        optionSelectionType: 'radio';
        dialogName:
          | 'Candidate Management - Placement - Update Hiring Status'
          | 'Candidate Management - Placement - Hiring Check-In';
        prospectInboxCount: number | null;
        prospectPrescreenCount: number | null;
        prospectShortlistCount: number | null;
        prospectInterviewCount: number | null;
        prospectOfferCount: number | null;
        prospectAcceptCount: number | null;
        prospectNotSuitableCount: number | null;
        hubbleExperiments: {
          cm_improve_direct_placement_signals: {
            name: 'cm_improve_direct_placement_signals';
            participantStatus: 'enrolled';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          };
        };
        experiments: [
          {
            num: 3;
            id: 'cm_improve_direct_placement_signals';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          },
        ];
      };
    }
  | {
      event: 'view_report_pressed';
      attributes: {
        seekListingId: number;
        linkAction: 'Redirect to Report';
        linkText: 'View report';
        dialogName: 'Candidate Management - Placement - Improve Job Ad';
        hubbleExperiments: {
          cm_improve_direct_placement_signals: {
            name: 'cm_improve_direct_placement_signals';
            participantStatus: 'enrolled';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          };
        };
        experiments: [
          {
            num: 3;
            id: 'cm_improve_direct_placement_signals';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          },
        ];
      };
    }
  | {
      event: 'bulk_actions_select_all_pressed';
      attributes: {
        seekListingId: number;
        objectInteraction: 'select-all';
        bulkActionCount: number;
      };
    }
  | {
      event: 'bulk_action_clear_selected_pressed';
      attributes: {
        seekListingId: number;
        bulkActionCount: 0;
        actionOrigin: 'Candidate Listing - Bulk Select Check Box';
      };
    }
  | {
      event: 'candidate_list_action_menu_pressed';
      attributes: {
        seekListingId: number;
      };
    }
  | {
      event: 'candidate_list_action_pressed';
      attributes: {
        seekListingId: number;
        linkAction:
          | 'select all on page'
          | 'select all in folder'
          | 'deselect all';
        bulkActionCount: number;
      };
    }
  | {
      event: 'content_element_pressed';
      attributes: {
        seekListingId: number;
        elementAction: 'article link clicked';
        elementText:
          | 'How to write a great job ad (with examples)'
          | 'Are candidates seeing your job ad? Make these changes'
          | 'Why your job ad may not be standing out'
          | "Losing candidates through the hiring process? Here's what to do"
          | 'Laws Of Attraction: Find out what New Zealand candidates want'
          | 'Make smarter data-driven decisions with Talent Attraction Lab';
        elementDesign: 'link';
        hubbleExperiments: {
          cm_improve_direct_placement_signals: {
            name: 'cm_improve_direct_placement_signals';
            participantStatus: 'enrolled';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          };
        };
        experiments: [
          {
            num: 3;
            id: 'cm_improve_direct_placement_signals';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          },
        ];
      };
    }
  | {
      event: 'job_upgrade_button_pressed';
      attributes: {
        seekListingId: number;
        linkAction: 'Redirect to Upgrade';
        linkText: 'Upgrade';
        dialogName: "Candidate Management - Placement - Can't Find The Right Candidate";
        hubbleExperiments: {
          cm_improve_direct_placement_signals: {
            name: 'cm_improve_direct_placement_signals';
            participantStatus: 'enrolled';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          };
        };
        experiments: [
          {
            num: 3;
            id: 'cm_improve_direct_placement_signals';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          },
        ];
      };
    }
  | {
      event: 'view_hiring_advice_pressed';
      attributes: {
        seekListingId: number;
        linkAction: 'Redirect to Article';
        linkText: 'View More Hiring Advice';
        dialogName: 'Candidate Management - Placement - Improve Next Job Ad';
        hubbleExperiments: {
          cm_improve_direct_placement_signals: {
            name: 'cm_improve_direct_placement_signals';
            participantStatus: 'enrolled';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          };
        };
        experiments: [
          {
            num: 3;
            id: 'cm_improve_direct_placement_signals';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          },
        ];
      };
    }
  | {
      event: 'hiring_in_progress_pressed';
      attributes: {
        seekListingId: number;
        hiringStatus:
          | 'Hiring status unavailable'
          | 'Role filled'
          | 'Role not filled'
          | 'Hiring in progress';
      };
    }
  | {
      event: 'verifications_in_candidate_list_pressed';
      attributes: {
        seekListingId: number;
        candidateId: number;
      };
    }
  | {
      event: 'order_reference_check_pressed';
      attributes: {
        isSeekReferenceJob: boolean;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCurrentPage;
        seekListingId: number;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
        jobSeekReferenceStatus: 'standard' | 'limited' | null;
        linkAction:
          | 'Order Reference Check'
          | 'Request a Limited Reference Check'
          | null;
        linkText: 'Order reference check';
        dialogName:
          | 'Candidate Management - Order a Reference Check'
          | 'Candidate Management - Request a Limited Reference Check'
          | null;
      };
    }
  | {
      event: 'purchase_reference_check_add_on_pressed';
      attributes: {
        actionOrigin: CheckReferencesActionOrigins;
        isSeekReferenceJob: boolean;
        jobSeekReferenceStatus: 'limited' | 'standard' | null;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCurrentPage;
        seekListingId: number;
        candidateId: number | null;
        prospectId: string | null;
        applicationCorrelationId: string | null;
        linkAction: 'Go To Purchase Reference Check Add On Modal';
        linkText: 'purchase add on';
        dialogName: 'Candidate Management - Unlock Full Reference Check Upgrade Now';
      };
    };

type CandidateManagementViewAnalyticEvents =
  | {
      event: 'candidate_management_freemium_pop_up_displayed';
      attributes: {
        seekListingId: number;
        alertShownMessageCode:
          | 'Candidate Management - Freemium Upgrade - Hard to Fill'
          | 'Candidate Management - Freemium Upgrade - Urgent Hiring';
      };
    }
  | {
      event: 'dialog_displayed';
      attributes: {
        seekListingId: number;
        currentPage?: TrackingCurrentPage;
        dialogName:
          | 'Candidate Management - How Reference Checks Work'
          | "Candidate Management - SEEKRef - Couldn't check references";
      };
    }
  | {
      event: 'dialog_displayed';
      attributes: {
        seekListingId: number;
        currentPage?: TrackingCurrentPage;
        dialogName:
          | 'Candidate Management - Placement - Update Hiring Status'
          | 'Candidate Management - Placement - Hiring Check-In'
          | 'Candidate Management - Placement - Improve Job Ad'
          | 'Candidate Management - Placement - Improve Next Job Ad'
          | "Candidate Management - Placement - Can't Find The Right Candidate"
          | "Candidate Management - Placement - Can't Display Hiring Status"
          | "Candidate Management - Placement - Couldn't Update Hiring Status";
        hubbleExperiments: {
          cm_improve_direct_placement_signals: {
            name: 'cm_improve_direct_placement_signals';
            participantStatus: 'enrolled';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          };
        };
        experiments: [
          {
            num: 3;
            id: 'cm_improve_direct_placement_signals';
            variant:
              | 'not_yet'
              | 'too_early_to_say'
              | 'still_reviewing_applications'
              | 'control';
          },
        ];
      };
    }
  | {
      event: 'dialog_displayed';
      attributes: {
        isSeekReferenceJob: boolean;
        seekReferenceBalance: number | null;
        bucket: AnalyticsApplicationStatus;
        currentPage: TrackingCurrentPage;
        seekListingId: number;
        candidateId: number;
        prospectId: string;
        applicationCorrelationId: string;
        dialogName:
          | 'Candidate Management - Order a Reference Check'
          | 'Candidate Management - Reference Check Requested'
          | 'Candidate Management - Request a Limited Reference Check'
          | 'Candidate Management - Limited Reference Check Requested'
          | null;
        jobSeekReferenceStatus: 'standard' | 'limited' | null;
      };
    }
  | {
      event: 'dialog_displayed';
      attributes: {
        dialogName: 'Candidate Management - Unlock Full Reference Check Upgrade Now';
        applicationCorrelationId: string | null;
        candidateId: number | null;
        prospectId: string | null;
        seekListingId: number;
        currentPage: TrackingCurrentPage;
        bucket: AnalyticsApplicationStatus;
        seekReferenceBalance: number | null;
        jobSeekReferenceStatus: 'limited' | 'standard' | null;
        isSeekReferenceJob: boolean;
        actionOrigin: CheckReferencesActionOrigins;
      };
    }
  | {
      event: 'alert_viewed';
      attributes: {
        alertShownType: 'alert';
        alertShownPosition: 'content';
        alertShownMessageCode:
          | 'some_high_fit_applicant_not_shown'
          | 'all_high_fit_applicant_not_shown';
      };
    }
  | {
      event: 'candidate_list_displayed';
      attributes: {
        seekListingId: number;
        highFitApplicant: `${number}:${number}`;
        hubbleExperiments: {
          cm_high_fit_applicant_explainability: {
            name: 'cm_high_fit_applicant_explainability';
            participantStatus: 'enrolled';
            variant: 'control' | 'challenger';
          };
        };
      };
    };

export const createPageAnalytics = createPageAnalyticsFn<
  CandidateManagementLinkAnalyticEvents,
  CandidateManagementViewAnalyticEvents
>({
  siteSubsection: 'candidate',
});

export type CandidatesManagementPageAnalytics = ReturnType<
  typeof createPageAnalytics
>;
